import React from 'react';
import { UseMutationResult } from 'react-query';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { FetcherParams, useWidgetsMutation } from '../../api/useWidgetsMutation';
import { WidgetDTO, WidgetsDTO } from '../../api/useWidgetsQuery';
import { TabSlug } from '../../types';

interface Props {
  tab: TabSlug;
  widgets: WidgetsDTO;
  widgetToExclude: WidgetDTO;
  onClose(): void;
}

export const ExcludeWidgetModal = ({ tab, widgetToExclude, widgets, onClose }: Props) => {
  const { mutateAsync, error, isLoading } = useWidgetsMutation({ tab });

  const onSave = async nextWidgets => {
    await mutateAsync({ tab, widgets: nextWidgets });
  };

  return (
    <ExcludeWidgetModalContent
      widgetToExclude={widgetToExclude}
      widgets={widgets}
      onClose={onClose}
      onSave={onSave}
      error={error}
      isLoading={isLoading}
    />
  );
};

interface ExcludeWidgetModalContentProps {
  widgetToExclude: WidgetDTO;
  widgets: WidgetsDTO;
  onClose(): void;
  onSave: (newWidgets: WidgetsDTO) => Promise<void> | void;
  error?: UseMutationResult<WidgetsDTO, Error, FetcherParams, unknown>['error'];
  isLoading?: UseMutationResult<WidgetsDTO, Error, FetcherParams, unknown>['isLoading'];
}

export const ExcludeWidgetModalContent = ({
  widgetToExclude,
  widgets,
  onClose,
  onSave,
  error,
  isLoading,
}: ExcludeWidgetModalContentProps) => {
  const excludeWidget = async () => {
    try {
      const nextWidgets = {
        included: widgets.included.filter(({ id }) => id !== widgetToExclude.id),
        excluded: [...widgets.excluded, widgetToExclude],
      };

      onSave(nextWidgets);

      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Modal isOpen onRequestClose={onClose} aria={{ labelledby: 'exclude-widget-modal--header' }}>
      <ModalHeader id="exclude-widget-modal--header">Delete Card?</ModalHeader>

      {error && <Alert message={error.message} variant="error" mb="16" mx="24" />}

      <ModalBody>
        Are you sure you want to delete{' '}
        <Text as="span" fontWeight={600}>
          {widgetToExclude.title}
        </Text>
        {'? '}
        This action cannot be undone.
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onClose} mr="8">
          Cancel
        </Button>

        <Button isLoading={isLoading} variant="error" onClick={excludeWidget}>
          Delete Card
        </Button>
      </ModalFooter>
    </Modal>
  );
};
